import React, { useState, useEffect } from 'react';
import { Globe, Plus, Trash2, CheckCircle2, Loader2, Eye, EyeOff, Save } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent } from '../components/ui/card';
import { Switch } from '../components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';

const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

function Sites() {
  const [sites, setSites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [testingId, setTestingId] = useState(null);
  const [showPasswords, setShowPasswords] = useState({});
  const [editedCredentials, setEditedCredentials] = useState({});
  const [savingId, setSavingId] = useState(null);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    type: '',
    base_url: '',
    username: '',
    password: '',
    enabled: true
  });

  useEffect(() => {
    fetchSites();
  }, []);

  const fetchSites = async () => {
    try {
      const response = await fetch(`${API_URL}/api/sites`);
      const data = await response.json();
      setSites(data);
      
      // Initialize edited credentials with current values
      const initialEdits = {};
      data.forEach(site => {
        initialEdits[site._id] = {
          username: site.username || '',
          password: site.password || ''
        };
      });
      setEditedCredentials(initialEdits);
    } catch (error) {
      console.error('Error fetching sites:', error);
      toast.error('Failed to load sites');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const response = await fetch(`${API_URL}/api/sites`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        toast.success('Site added successfully');
        setDialogOpen(false);
        setFormData({ name: '', type: '', base_url: '', username: '', password: '', enabled: true });
        setShowNewPassword(false);
        fetchSites();
      } else {
        toast.error('Failed to add site');
      }
    } catch (error) {
      toast.error('Error adding site');
    }
  };

  const handleToggle = async (siteId, enabled) => {
    try {
      await fetch(`${API_URL}/api/sites/${siteId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ enabled: !enabled })
      });
      fetchSites();
      toast.success(`Site ${!enabled ? 'enabled' : 'disabled'}`);
    } catch (error) {
      toast.error('Failed to update site');
    }
  };

  const handleTestConnection = async (siteId) => {
    setTestingId(siteId);
    try {
      const response = await fetch(`${API_URL}/api/sites/${siteId}/test`, {
        method: 'POST'
      });
      const result = await response.json();

      if (result.status === 'success') {
        toast.success('Connection successful', { description: result.message });
      } else {
        toast.error('Connection failed', { description: result.message });
      }
    } catch (error) {
      toast.error('Test failed', { description: error.message });
    } finally {
      setTestingId(null);
    }
  };

  const handleDelete = async (siteId) => {
    if (!window.confirm('Are you sure you want to delete this site?')) return;

    try {
      await fetch(`${API_URL}/api/sites/${siteId}`, { method: 'DELETE' });
      toast.success('Site deleted');
      fetchSites();
    } catch (error) {
      toast.error('Failed to delete site');
    }
  };

  // Handle local credential changes (no API call)
  const handleCredentialChange = (siteId, field, value) => {
    setEditedCredentials(prev => ({
      ...prev,
      [siteId]: {
        ...prev[siteId],
        [field]: value
      }
    }));
  };

  // Save credentials to server
  const handleSaveCredentials = async (siteId) => {
    setSavingId(siteId);
    try {
      const credentials = editedCredentials[siteId];
      await fetch(`${API_URL}/api/sites/${siteId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          username: credentials.username,
          password: credentials.password
        })
      });
      toast.success('Credentials saved');
      fetchSites();
    } catch (error) {
      toast.error('Failed to save credentials');
    } finally {
      setSavingId(null);
    }
  };

  // Check if credentials have been modified
  const hasCredentialChanges = (siteId, site) => {
    const edited = editedCredentials[siteId];
    if (!edited) return false;
    return edited.username !== (site.username || '') || edited.password !== (site.password || '');
  };

  // Toggle password visibility
  const togglePasswordVisibility = (siteId) => {
    setShowPasswords(prev => ({
      ...prev,
      [siteId]: !prev[siteId]
    }));
  };

  if (loading) {
    return <div className="loading-spinner" />;
  }

  return (
    <div data-testid="sites-page">
      <div className="section-header">
        <div>
          <h2 className="section-title">Tender Sites</h2>
          <p style={{ color: 'hsl(var(--muted-foreground))', fontSize: 14, marginTop: 4 }}>
            Configure the tender portals to scrape
          </p>
        </div>
        <Button onClick={() => setDialogOpen(true)} data-testid="add-site-button">
          <Plus size={16} />
          Add Site
        </Button>
      </div>

      <div style={{ display: 'grid', gap: 16 }}>
        {sites.map((site) => (
          <Card key={site._id} data-testid={`site-card-${site._id}`}>
            <CardContent style={{ padding: 20 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <div style={{ display: 'flex', gap: 16, alignItems: 'flex-start' }}>
                  <div
                    style={{
                      width: 48,
                      height: 48,
                      borderRadius: 12,
                      background: site.enabled ? 'hsl(142 72% 95%)' : 'hsl(var(--muted))',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: site.enabled ? 'hsl(142 72% 29%)' : 'hsl(var(--muted-foreground))'
                    }}
                  >
                    <Globe size={24} />
                  </div>
                  <div>
                    <h3 style={{ fontWeight: 600, fontSize: 16, marginBottom: 4 }}>{site.name}</h3>
                    <p style={{ color: 'hsl(var(--muted-foreground))', fontSize: 13 }}>
                      {site.base_url}
                    </p>
                    <div style={{ marginTop: 8, display: 'flex', gap: 8, alignItems: 'center' }}>
                      <span
                        className={`status-badge ${site.enabled ? 'status-completed' : 'status-pending'}`}
                      >
                        {site.enabled ? 'Enabled' : 'Disabled'}
                      </span>
                      <span style={{ color: 'hsl(var(--muted-foreground))', fontSize: 12 }}>
                        Type: {site.type}
                      </span>
                    </div>
                  </div>
                </div>

                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <Switch
                    checked={site.enabled}
                    onCheckedChange={() => handleToggle(site._id, site.enabled)}
                    data-testid={`site-toggle-${site._id}`}
                  />
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => handleTestConnection(site._id)}
                    disabled={testingId === site._id}
                    data-testid="sites-test-connection-button"
                  >
                    {testingId === site._id ? (
                      <Loader2 size={14} className="animate-spin" />
                    ) : (
                      <CheckCircle2 size={14} />
                    )}
                    Test
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleDelete(site._id)}
                    style={{ color: 'hsl(var(--error))' }}
                    data-testid={`delete-site-${site._id}`}
                  >
                    <Trash2 size={14} />
                  </Button>
                </div>
              </div>

              {/* Credentials Section */}
              <div style={{ marginTop: 20, paddingTop: 20, borderTop: '1px solid hsl(var(--border))' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                  <h4 style={{ fontSize: 13, fontWeight: 600, color: 'hsl(var(--muted-foreground))' }}>
                    Credentials
                  </h4>
                  {hasCredentialChanges(site._id, site) && (
                    <Button
                      size="sm"
                      onClick={() => handleSaveCredentials(site._id)}
                      disabled={savingId === site._id}
                      data-testid={`save-credentials-${site._id}`}
                    >
                      {savingId === site._id ? (
                        <Loader2 size={14} className="animate-spin" />
                      ) : (
                        <Save size={14} />
                      )}
                      Save Credentials
                    </Button>
                  )}
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                  <div className="form-group" style={{ marginBottom: 0 }}>
                    <label className="form-label">Username / Email</label>
                    <Input
                      type="text"
                      value={editedCredentials[site._id]?.username || ''}
                      onChange={(e) => handleCredentialChange(site._id, 'username', e.target.value)}
                      placeholder="Enter username"
                      data-testid={`username-input-${site._id}`}
                    />
                  </div>
                  <div className="form-group" style={{ marginBottom: 0 }}>
                    <label className="form-label">Password</label>
                    <div style={{ position: 'relative', display: 'flex', gap: 8 }}>
                      <Input
                        type={showPasswords[site._id] ? 'text' : 'password'}
                        value={editedCredentials[site._id]?.password || ''}
                        onChange={(e) => handleCredentialChange(site._id, 'password', e.target.value)}
                        placeholder="Enter password"
                        style={{ flex: 1 }}
                        data-testid={`password-input-${site._id}`}
                      />
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => togglePasswordVisibility(site._id)}
                        style={{ padding: '0 12px' }}
                        data-testid={`toggle-password-${site._id}`}
                      >
                        {showPasswords[site._id] ? <EyeOff size={16} /> : <Eye size={16} />}
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Add Site Dialog */}
      <Dialog open={dialogOpen} onOpenChange={(open) => {
        setDialogOpen(open);
        if (!open) setShowNewPassword(false);
      }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add New Site</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label className="form-label">Site Name</label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                placeholder="e.g., Ethio Jobs"
                required
                data-testid="new-site-name"
              />
            </div>
            <div className="form-group">
              <label className="form-label">Type (identifier)</label>
              <Input
                value={formData.type}
                onChange={(e) => setFormData(prev => ({ ...prev, type: e.target.value }))}
                placeholder="e.g., ethiojobs"
                required
                data-testid="new-site-type"
              />
            </div>
            <div className="form-group">
              <label className="form-label">Base URL</label>
              <Input
                value={formData.base_url}
                onChange={(e) => setFormData(prev => ({ ...prev, base_url: e.target.value }))}
                placeholder="https://www.ethiojobs.net"
                required
                data-testid="new-site-url"
              />
            </div>
            <div className="form-group">
              <label className="form-label">Username / Email (optional)</label>
              <Input
                type="text"
                value={formData.username}
                onChange={(e) => setFormData(prev => ({ ...prev, username: e.target.value }))}
                placeholder="Enter username"
                data-testid="new-site-username"
              />
            </div>
            <div className="form-group">
              <label className="form-label">Password (optional)</label>
              <div style={{ position: 'relative', display: 'flex', gap: 8 }}>
                <Input
                  type={showNewPassword ? 'text' : 'password'}
                  value={formData.password}
                  onChange={(e) => setFormData(prev => ({ ...prev, password: e.target.value }))}
                  placeholder="Enter password"
                  style={{ flex: 1 }}
                  data-testid="new-site-password"
                />
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setShowNewPassword(!showNewPassword)}
                  style={{ padding: '0 12px' }}
                  data-testid="toggle-new-password"
                >
                  {showNewPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                </Button>
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="secondary" onClick={() => setDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" data-testid="submit-new-site">Add Site</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default Sites;
